import React from "react";
import { CheckCircle2, Clock, AlertCircle, ChevronRight, MoreHorizontal } from "lucide-react";

/**
 * MomentCard — the visible NOW card for the Guide Journey.
 *
 * Internal concept: Moment. Visible title: NOW (rendered as a branded
 * label in all caps per Toto conventions).
 *
 * NOW is not a clock-based task list. It surfaces responsibilities the
 * signed-in Guide is expected to act on, based on shift stage, room
 * state, and completion evidence. Items remain until an expected result
 * is recorded — passing a scheduled time is not completion.
 *
 * The component is deliberately stateless above the item-toggle level.
 * The parent owns the fetch cycle and re-fetches after any action.
 */

const PRIORITY_STYLE = {
  urgent:    { pill: "bg-amber-100 text-amber-800",  ring: "ring-amber-200" },
  important: { pill: "bg-stone-100 text-stone-700",  ring: "ring-stone-200" },
  standard:  { pill: "bg-transparent text-stone-500", ring: "ring-transparent" },
};

const PRIORITY_LABEL = {
  urgent: "Elevated",
  important: "In progress",
  standard: "",
};

export default function MomentCard({ moments, onCheckToggle, busyKey }) {
  const active = (moments || []).filter((m) => m.status === "active" || m.status === "waiting");
  return (
    <section
      className="bg-white subtle-border rounded-2xl p-8 card-shadow"
      data-testid="moment-card"
      aria-labelledby="moment-card-title"
    >
      <header className="flex items-baseline justify-between mb-6">
        <div>
          <div className="text-[10px] label-eyebrow text-stone-400">Guide Journey</div>
          <h2
            id="moment-card-title"
            className="font-serif-display text-3xl text-stone-900 mt-1 tracking-wide"
            style={{ fontWeight: 400 }}
          >
            NOW
          </h2>
        </div>
        <div className="text-[10px] label-eyebrow text-stone-400">
          {active.length === 0 ? "quiet" : `${active.length} waiting on you`}
        </div>
      </header>

      {active.length === 0 ? (
        <EmptyState />
      ) : (
        <ul className="divide-y divide-stone-100" data-testid="moment-list">
          {active.map((m) => (
            <MomentRow
              key={m.id}
              moment={m}
              onCheckToggle={onCheckToggle}
              busyKey={busyKey}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function EmptyState() {
  return (
    <div
      className="rounded-xl bg-stone-50/60 subtle-border py-8 px-6 text-center"
      data-testid="moment-empty"
    >
      <div className="w-9 h-9 rounded-full bg-white subtle-border mx-auto flex items-center justify-center text-stone-400">
        <CheckCircle2 size={16} strokeWidth={1.25} />
      </div>
      <div className="font-serif-display text-stone-900 mt-3 text-lg" style={{ fontWeight: 400 }}>
        Nothing is waiting on you right now.
      </div>
      <p className="text-xs text-stone-500 mt-2 max-w-xs mx-auto leading-relaxed">
        NOW will speak when a responsibility needs your hands.
      </p>
    </div>
  );
}

function MomentRow({ moment, onCheckToggle, busyKey }) {
  const priorityStyle = PRIORITY_STYLE[moment.priority] || PRIORITY_STYLE.standard;
  const priorityLabel = PRIORITY_LABEL[moment.priority];
  const checks = moment.metadata?.checks || [];
  const testId = `moment-row-${moment.kind}-${moment.metadata?.room || moment.metadata?.checks?.[0]?.key || moment.id}`;

  return (
    <li className="py-5 first:pt-0 last:pb-0" data-testid={testId}>
      <div className="flex items-start gap-4">
        <StatusDot priority={moment.priority} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm text-stone-900" style={{ fontWeight: 500 }}>
              {moment.title}
            </div>
            {priorityLabel && (
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${priorityStyle.pill}`}>
                {priorityLabel}
              </span>
            )}
          </div>
          {moment.description && (
            <p className="text-xs text-stone-500 mt-1 leading-relaxed">{moment.description}</p>
          )}

          {checks.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {checks.map((c) => (
                <CheckPill
                  key={c.key}
                  check={c}
                  onToggle={() => onCheckToggle && onCheckToggle(c.key, !c.done)}
                  busy={busyKey === c.key}
                />
              ))}
            </div>
          )}

          {moment.kind === "room_readiness" && (
            <div className="mt-3 text-[11px] text-stone-500 leading-relaxed">
              Completion is recorded when a room-reset entry is logged for room {moment.metadata?.room}.
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function StatusDot({ priority }) {
  if (priority === "urgent") {
    return (
      <span className="shrink-0 mt-1.5 w-2 h-2 rounded-full bg-amber-400 ring-4 ring-amber-100" aria-hidden="true" />
    );
  }
  if (priority === "important") {
    return (
      <span className="shrink-0 mt-1.5 w-2 h-2 rounded-full bg-stone-500" aria-hidden="true" />
    );
  }
  return (
    <span className="shrink-0 mt-1.5 w-2 h-2 rounded-full bg-stone-300" aria-hidden="true" />
  );
}

function CheckPill({ check, onToggle, busy }) {
  const done = !!check.done;
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={busy}
      data-testid={`moment-check-${check.key}`}
      className={`inline-flex items-center gap-2 pl-2 pr-3 py-1 rounded-full text-xs subtle-border transition-colors ${
        done
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-white text-stone-700 hover:bg-stone-50"
      } ${busy ? "opacity-60" : ""}`}
      title={done ? "Mark not done" : "Mark done"}
    >
      <span
        className={`w-4 h-4 rounded-full border flex items-center justify-center ${
          done ? "bg-emerald-500 border-emerald-500 text-white" : "border-stone-300"
        }`}
      >
        {done && <CheckCircle2 size={11} strokeWidth={2} />}
      </span>
      <span>{check.label}</span>
    </button>
  );
}
